import { useQuery } from "@tanstack/react-query";
import { facilityApi } from "@bw/shared/api";
import type { PublicFacilitySearchResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

export interface BookableFacilitiesParams {
  query?: string;
  city?: string;
  service?:
    | "court_reservation"
    | "open_play"
    | "coaching"
    | "classes"
    | "memberships";
  /** Optional center → distance_km + nearest-first sort (facility-discovery B1). */
  latitude?: number;
  longitude?: number;
  radius_km?: number;
  open_now?: boolean;
  special_offers?: boolean;
  min_courts?: number;
  limit?: number;
  offset?: number;
}

/**
 * The bookable-facility list for the facility picker. Wraps
 * `facilityApi.search` (GET /facilities/search) — the public discovery list of
 * facilities a player can browse. Honest empty result when nothing matches.
 *
 * Optional lat/lng/radius_km add a real distance_km + a nearest-first sort
 * (FacilityPickerCard renders the distance; the FE never computes it). The
 * backend strips precise coords for an unconsented-minor token (ADR-024).
 */
export function useFacilities(params: BookableFacilitiesParams = {}) {
  return useQuery<PublicFacilitySearchResponse>({
    queryKey: [...queryKeys.booking.facilities(params), "page"],
    refetchInterval: params.open_now ? 60_000 : false,
    queryFn: async () => {
      const res = await facilityApi.search({
        query: params.query,
        city: params.city,
        service: params.service,
        latitude: params.latitude,
        longitude: params.longitude,
        radius_km: params.radius_km,
        open_now: params.open_now,
        special_offers: params.special_offers,
        ...(params.min_courts ? { min_courts: params.min_courts } : {}),
        limit: params.limit,
        offset: params.offset,
      });
      return res;
    },
  });
}
